// SelectionComponents.jsx
import React from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Popover,
} from "@mui/material";
import DropDownWithSearch from "./DropDownWithSearch";

const bgColor = "rgb(210 210 210 / 10%)";
const fullBgColor = {
  "& .MuiFilledInput-root": {
    bgcolor: bgColor,
  },
  "& .MuiSelect-select": {
    // Targets the select input itself
    bgcolor: bgColor,
  },
  "&.MuiFilledInput-root": {
    // Targets the root when Select is used directly
    bgcolor: bgColor,
  },
};

export const TypeSelectionSection = ({
  patientTypeDropdown,
  orderTypeDropdown,
  setPatientType,
  setpatientTypeDropdown,
  setOrderType,
  setorderTypeDropdown,
  summaryParamsErrorMessage,
  setSummaryParamsErrorMessage,
}) => (
  <Box>
    <Typography sx={{ fontSize: "1rem" }} className="mb-3">
      Patient and Order Selection
    </Typography>
    <Stack spacing={2}>
      {/* Patient Type Selection */}
      <Grid item xs={12} md={12}>
        <FormControl
          fullWidth
          error={Boolean(summaryParamsErrorMessage?.patientType)}
        >
          <InputLabel required>Patient Type</InputLabel>
          <Select
            value={patientTypeDropdown}
            label="Patient Type *"
            sx={fullBgColor}
            variant="filled"
            size="small"
            onChange={(e) => {
              if (e.target.value === "New") {
                setPatientType("New");
                setpatientTypeDropdown("New");
                setOrderType("New");
                setorderTypeDropdown("New");
              } else {
                setPatientType("Existing");
                setpatientTypeDropdown("Existing");
              }
              setSummaryParamsErrorMessage({
                ...summaryParamsErrorMessage,
                patientType: "",
                selectedPatient: "",
              });
            }}
          >
            <MenuItem value="New">New</MenuItem>
            <MenuItem value="Existing">Existing</MenuItem>
          </Select>
          {summaryParamsErrorMessage?.patientType && (
            <FormHelperText>
              {summaryParamsErrorMessage.patientType}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Order Type Selection */}
      <Grid item xs={12} md={12}>
        <FormControl
          fullWidth
          error={Boolean(summaryParamsErrorMessage?.orderType)}
        >
          <InputLabel required>Order Type</InputLabel>
          <Select
            value={orderTypeDropdown}
            size="small"
            sx={fullBgColor}
            variant="filled"
            label="Order Type *"
            onChange={(e) => {
              if (e.target.value === "New") {
                setOrderType("New");
                setorderTypeDropdown("New");
              } else {
                setOrderType("Existing");
                setorderTypeDropdown("Existing");
                setSummaryParamsErrorMessage({
                  ...summaryParamsErrorMessage,
                  selectedOrder: "",
                });
              }
            }}
          >
            <MenuItem value="New">New</MenuItem>
            <MenuItem value="Existing">Existing</MenuItem>
          </Select>
          {summaryParamsErrorMessage?.orderType && (
            <FormHelperText>
              {summaryParamsErrorMessage.orderType}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Stack>
  </Box>
);

export const PatientDetailsSection = ({
  patientTypeDropdown,
  selectedPatientValue,
  summaryParamsErrorMessage,
  patientDetailAnchorEl,
  setPatientDetailAnchorEl,
  dropDownSelectPatient,
}) => (
  <>
    {patientTypeDropdown === "Existing" && (
      <Box>
        <Typography sx={{ fontSize: "1rem" }} className="mb-0">
          Patient Details
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box flexGrow={1}>
            <Typography
              variant="subtitle2"
              color={
                !selectedPatientValue?.data?.id &&
                summaryParamsErrorMessage?.selectedPatient
                  ? "error"
                  : "textPrimary"
              }
              className="flex items-center"
            >
              {selectedPatientValue?.name?.length > 2 && (
                <>
                  Patient Name
                  <Typography component="span">&nbsp;*</Typography>
                </>
              )}
            </Typography>
          </Box>
        </Stack>

        <Popover
          open={Boolean(patientDetailAnchorEl)}
          anchorEl={patientDetailAnchorEl}
          onClose={() => setPatientDetailAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Paper className="p-4 max-w-sm">
            {/* ... Patient details content ... */}
          </Paper>
        </Popover>
        <DropDownWithSearch
          setselectedValue={dropDownSelectPatient}
          selectedValue={selectedPatientValue}
          placeholder="Select Another Patient"
          drpdwn="patientList"
          summaryParamsErrorMessage={summaryParamsErrorMessage}
          inputPlaceHolder="Search Patient/DOB(mm/dd/yyyy)"
        />
      </Box>
    )}
  </>
);

export const OrderDetailsSection = ({
  orderTypeDropdown,
  selectedOrderValue,
  summaryParamsErrorMessage,
  selectedDocumentValue,
  setselectedDocumentValue,
  setSummaryParamsErrorMessage,
  documentTypeDropdownList,
  dropDownSelectOrder,
  selectedPatientValue,
  therapyTypeDropdown,
  settherapyTypeDropdown,
  therapyTypeDropdownList,
  PriorityDropdown,
  setPriorityDropdown,
}) => (
  <>
    {orderTypeDropdown === "Existing" ? (
      <Box>
        <Typography
          sx={{
            fontSize: "1rem",
          }}
          className="mb-2"
        >
          Order Information
        </Typography>
        <Typography
          variant="subtitle2"
          color={
            !selectedOrderValue?.data?.id &&
            summaryParamsErrorMessage?.selectedOrder
              ? "error"
              : "textPrimary"
          }
          className="flex items-center"
        >
          Order ID
          <Typography component="span">&nbsp;*</Typography>
        </Typography>

        <DropDownWithSearch
          setselectedValue={dropDownSelectOrder}
          selectedValue={selectedOrderValue}
          placeholder="Select Another Order"
          drpdwn="orderList"
          summaryParamsErrorMessage={summaryParamsErrorMessage}
          displayedPatient={selectedPatientValue.data?.id}
          inputPlaceHolder="Search Order"
        />

        {/* Document Type Selection */}
        <Grid mt={2} item xs={12} md={12}>
          <FormControl
            fullWidth
            error={Boolean(summaryParamsErrorMessage?.documentType)}
          >
            <InputLabel required>Document Type</InputLabel>
            <Select
              value={selectedDocumentValue || ""}
              size="small"
              label="Document Type *"
              sx={fullBgColor}
              variant="filled"
              onChange={(e) => {
                setselectedDocumentValue(e.target.value);
                setSummaryParamsErrorMessage({
                  ...summaryParamsErrorMessage,
                  documentType: "",
                });
              }}
            >
              {documentTypeDropdownList?.map((ele, ind) => (
                <MenuItem key={ind} value={ele.documentType}>
                  {ele.documentType}
                </MenuItem>
              ))}
            </Select>
            {summaryParamsErrorMessage?.documentType && (
              <FormHelperText>
                {summaryParamsErrorMessage.documentType}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Box>
    ) : (
      // New Order Additional Fields
      <Box>
        <Typography
          sx={{
            fontSize: "1rem",
          }}
          className="mb-3"
        >
          Order Details
        </Typography>
        <Stack spacing={2}>
          {/* Product Type */}
          <Grid item xs={12} md={12}>
            <FormControl
              fullWidth
              error={!!summaryParamsErrorMessage?.therapyType}
            >
              <InputLabel required>Product Type</InputLabel>
              <Select
                size="small"
                value={therapyTypeDropdown}
                sx={fullBgColor}
                variant="filled"
                label="Product Type *"
                onChange={(e) => settherapyTypeDropdown(e.target.value)}
              >
                {therapyTypeDropdownList?.map((ele, ind) => (
                  <MenuItem key={ind} value={ele.Type}>
                    {ele.Type}
                  </MenuItem>
                ))}
              </Select>
              {summaryParamsErrorMessage?.therapyType && (
                <FormHelperText>
                  {summaryParamsErrorMessage.therapyType}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Document Type Selection */}
          <Grid item xs={12} md={12}>
            <FormControl
              fullWidth
              error={Boolean(summaryParamsErrorMessage?.documentType)}
            >
              <InputLabel required>Document Type</InputLabel>
              <Select
                size="small"
                sx={fullBgColor}
                variant="filled"
                value={selectedDocumentValue || ""}
                label="Document Type *"
                onChange={(e) => {
                  setselectedDocumentValue(e.target.value);
                  setSummaryParamsErrorMessage({
                    ...summaryParamsErrorMessage,
                    documentType: "",
                  });
                }}
              >
                {documentTypeDropdownList?.map((ele, ind) => (
                  <MenuItem key={ind} value={ele.documentType}>
                    {ele.documentType}
                  </MenuItem>
                ))}
              </Select>
              {summaryParamsErrorMessage?.documentType && (
                <FormHelperText>
                  {summaryParamsErrorMessage.documentType}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Priority Selection */}
          <Grid item xs={12} md={12}>
            <FormControl
              fullWidth
              error={Boolean(summaryParamsErrorMessage?.priorityL)}
            >
              <InputLabel required>Priority</InputLabel>
              <Select
                size="small"
                sx={fullBgColor}
                variant="filled"
                value={PriorityDropdown}
                label="Priority *"
                onChange={(e) => {
                  setPriorityDropdown(e.target.value);
                  setSummaryParamsErrorMessage({
                    ...summaryParamsErrorMessage,
                    priorityL: "",
                  });
                }}
              >
                {["Urgent", "High", "Normal", "Low"].map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
              {summaryParamsErrorMessage?.priorityL && (
                <FormHelperText>
                  {summaryParamsErrorMessage.priorityL}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Stack>
      </Box>
    )}
  </>
);

export const ProviderDetailsSection = ({
  orderTypeDropdown,
  providerTypeDropdown,
  summaryParamsErrorMessage,
  setProviderType,
  setproviderTypeDropdown,
  setselectedProviderLocation,
  setSummaryParamsErrorMessage,
  selectedProviderValue,
  dropDownSelectProvider,
  locationProviderDropdown,
  selectedProviderLocation,
  dropDownSelectProviderLocation,
  getproviderLocation,
}) => {
  const allOptions = locationProviderDropdown?.length
    ? locationProviderDropdown
    : selectedProviderLocation?.name
    ? [
        {
          name: selectedProviderLocation.name,
          data: selectedProviderLocation.data,
        },
      ]
    : [];

  return (
    <>
      {orderTypeDropdown !== "Existing" &&
        providerTypeDropdown === "Existing" && (
          <Box>
            <Typography
              sx={{
                fontSize: "1rem",
              }}
              className="mb-3"
            >
              Provider Information
            </Typography>
            <Stack spacing={2}>
              {/* Provider Type */}
              <Grid item xs={12} md={12}>
                <FormControl
                  fullWidth
                  error={Boolean(summaryParamsErrorMessage?.providerType)}
                >
                  <InputLabel required>Provider Type</InputLabel>
                  <Select
                    size="small"
                    sx={fullBgColor}
                    variant="filled"
                    value={providerTypeDropdown}
                    label="Provider Type *"
                    onChange={(e) => {
                      if (e.target.value === "New") {
                        setProviderType("New");
                        setproviderTypeDropdown("New");
                      } else {
                        setProviderType("Existing");
                        setproviderTypeDropdown("Existing");
                      }
                      setselectedProviderLocation({});
                      setSummaryParamsErrorMessage({
                        ...summaryParamsErrorMessage,
                        providerType: "",
                        selectedProvider: "",
                        providerLocation: "",
                      });
                    }}
                  >
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="Existing">Existing</MenuItem>
                  </Select>
                  {summaryParamsErrorMessage?.providerType && (
                    <FormHelperText>
                      {summaryParamsErrorMessage.providerType}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Provider Selection */}
              <Grid item xs={12} md={12}>
                <Typography
                  variant="subtitle2"
                  color={
                    !selectedProviderValue?.data?.id &&
                    summaryParamsErrorMessage?.selectedProvider
                      ? "error"
                      : "textPrimary"
                  }
                  className="flex items-center"
                >
                  {selectedProviderValue?.name?.length > 2 && (
                    <>
                      Provider Name
                      <Typography component="span">&nbsp;*</Typography>
                    </>
                  )}
                </Typography>
                <DropDownWithSearch
                  setselectedValue={dropDownSelectProvider}
                  selectedValue={selectedProviderValue}
                  placeholder="Select Another Provider"
                  drpdwn="providerList"
                  summaryParamsErrorMessage={summaryParamsErrorMessage}
                  inputPlaceHolder="Search Provider"
                />
              </Grid>

              {/* Provider Location */}
              {/* <Grid item xs={12} md={12}>
                <Box>
                  <FormControl
                    fullWidth
                    error={Boolean(summaryParamsErrorMessage?.providerLocation)}
                  >
                    <InputLabel required>Provider Location</InputLabel>
                    <Select
                      size="small"
                      displayEmpty
                      value={selectedProviderLocation?.name || ""}
                      onChange={(e) => {
                        const location = locationProviderDropdown.find(
                          (loc) => loc.name === e.target.value,
                        );
                        setselectedProviderLocation(location);
                        setSummaryParamsErrorMessage({
                          ...summaryParamsErrorMessage,
                          providerLocation: "",
                        });
                        dropDownSelectProviderLocation(location);
                      }}
                      onClick={() => {
                        if (selectedProviderValue?.data?.id) {
                          getproviderLocation(selectedProviderValue?.data?.id);
                        }
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Provider Location
                      </MenuItem>
                      {allOptions.map((ele, ind) => (
                        <MenuItem
                          key={ind}
                          value={ele.name}
                          sx={{
                            color:
                              ele.name === selectedProviderLocation?.name
                                ? "primary.main"
                                : "inherit",
                          }}
                        >
                          {ele.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {summaryParamsErrorMessage?.providerLocation && (
                      <FormHelperText>
                        {summaryParamsErrorMessage.providerLocation}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Grid> */}
            </Stack>
          </Box>
        )}
    </>
  );
};
